/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { animated, useSpring } from '@react-spring/web';
import useSize from '@hzdg/use-size';
import * as Icons from '@tishman/icons';

import {
  Flex,
  CarouselV2ContextProvider,
  CarouselV2,
  CarouselNavigationV2,
  Button,
  AnimationState,
  useAnimationStateSelector,
  usePageTransitionActions,
  usePageTransitionDispatch,
  useThemeUI,
} from '..';

import {
  ModalSocialCarouselCard,
  ModalSocialCarouselCardProps,
} from './ModalSocialCarouselCard';

const ModalSocialCarouselList = ({
  cards,
  initialIndex = 0,
}: {
  cards: ModalSocialCarouselCardProps[];
  initialIndex?: number;
}): React.JSX.Element => (
  <CarouselV2 swiperOptions={{ initialSlide: initialIndex }}>
    {cards.map((card, index) => (
      <ModalSocialCarouselCard key={card.id} {...card} data-index={index} />
    ))}
  </CarouselV2>
);

export const ModalSocialCarousel = ({
  cards,
  closeModal,
  initialIndex = 0,
}: {
  cards: ModalSocialCarouselCardProps[];
  closeModal: () => void;
  initialIndex?: number;
}): React.JSX.Element => {
  const themeUI = useThemeUI();
  const actions = usePageTransitionActions();
  const dispatch = usePageTransitionDispatch();

  const [{ width: modalWidth }, sizeRef] = useSize();

  const ctrlIds = useRef(new Set<number>());

  const [transition, ref] = useSpring((i, ctrl) => {
    ctrlIds.current.add(ctrl.id);

    return {
      x: '100%',
    };
  });

  useEffect(() => {
    const ctrlIds_ = ctrlIds.current;

    ctrlIds_.forEach((animationId) => {
      dispatch(
        actions.add({
          animationId,
          animationState: AnimationState.IS_PENDING,
        }),
      );
    });

    return () => {
      ctrlIds_.forEach((animationId) => {
        dispatch(actions.remove(animationId));
      });
    };
  }, [actions, dispatch]);

  const animationState = useAnimationStateSelector([...ctrlIds.current]);

  useLayoutEffect(() => {
    switch (animationState) {
      case AnimationState.SHOULD_ENTER: {
        ref.start({
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_ENTERING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_ENTER,
              }),
            );
          },
          x: '0%',
        });
        break;
      }
      case AnimationState.SHOULD_LEAVE: {
        ref.start({
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_LEAVING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_LEAVE,
              }),
            );
          },
          x: '100%',
        });
        break;
      }
    }
  }, [actions, animationState, dispatch, ref]);

  return (
    <animated.div
      aria-label="Image Details"
      ref={sizeRef}
      role="dialog"
      style={{
        ...transition,
        background: themeUI.theme.colors.background,
        position: 'relative',
        placeSelf: 'stretch right',
        maxWidth: 515,
        width: '90%',
        overflow: 'scroll',
      }}
    >
      <Button
        onClick={closeModal}
        sx={{
          'color': 'text',
          'background': 'transparent',
          'position': 'absolute',
          'zIndex': 'overlay',
          'top': 0,
          'right': 0,
          'p': 3,
          'border': 'none',
          'fontSize': '0px',
          '&:hover': {
            background: 'transparent !important',
            svg: {
              transform: 'scale(1.1)',
            },
          },
        }}
      >
        <Icons.Close
          style={{
            transition: 'transform 0.3s ease-in-out',
          }}
        />
      </Button>
      <CarouselV2ContextProvider initialIndex={initialIndex}>
        <Flex
          sx={{
            position: 'absolute',
            right: 0,
            bg: 'background',
            height: 55, // same height as authorScreenName box
            zIndex: 'sticky',
            top: (theme) => {
              // Here is some sorcery
              // we need to absolutely position the navigation
              // under the image and to the right of the author
              // I used useSize to get the width of the modal
              // used the image ratio pulled from ./ModalSocialCarouselCard
              // calculated the bottom of image and added the margins to match
              // the authorScreenName margin
              const imageRatio = 350 / 515;
              const imageBottom = modalWidth * imageRatio;
              const mobileMargin = Array.isArray(theme.space)
                ? Number(theme.space[4])
                : 32;
              const desktopMargin = Array.isArray(theme.space)
                ? Number(theme.space[6])
                : 64;
              return [
                imageBottom + mobileMargin,
                null,
                imageBottom + desktopMargin,
              ];
            },
          }}
        >
          <CarouselNavigationV2 sx={{ mr: [3, null, 5] }} />
        </Flex>
        <ModalSocialCarouselList cards={cards} initialIndex={initialIndex} />
      </CarouselV2ContextProvider>
    </animated.div>
  );
};
